import { FC } from "react";
import styled from "@emotion/styled";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon";

export type SystemAudioPermissionModalRowStatus = {
  label: string;
  variant: MDSBadgeVariant;
  icon?: MdsIconKind;
};

export type SystemAudioPermissionModalRowAction = {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  variant?: MDSButtonVariant;
  isDisabled?: boolean;
};

export type SystemAudioPermissionModalRow = {
  id: string;
  index: number;
  label: string;
  supportingText?: string;
  status?: SystemAudioPermissionModalRowStatus;
  action?: SystemAudioPermissionModalRowAction;
};

export type SystemAudioPermissionModalRestartRow = {
  index: number;
  label: string;
  actionLabel: string;
  onClick: () => void;
  isLoading?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  rows: SystemAudioPermissionModalRow[];
  restartRow?: SystemAudioPermissionModalRestartRow;
  moreInfoHref?: string;
  onMoreInfoClick?: () => void;
};

export const SystemAudioPermissionModalContent: FC<Props> = ({
  isOpen,
  onClose,
  rows,
  restartRow,
}) => {
  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <Header slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          Let Mem take notes for you
        </MDSText>
      </Header>

      <DialogContent slot={MDSDialogSlotName.Content}>
        <Description variant={MDSTextVariant.ParagraphS}>
          Mem can record and create notes from meeting audio, podcasts, or simply your voice. Grant
          the required permissions in your system settings.
        </Description>

        <StyledDivider data-testid="system-audio-permission-divider" />

        <StyledRowList>
          {rows.map((row, index) => {
            const showDivider = index < rows.length - 1 || Boolean(restartRow);

            return (
              <StyledRowWrapper key={row.id}>
                <SystemAudioPermissionRow {...row} />
                {showDivider && <StyledRowDivider />}
              </StyledRowWrapper>
            );
          })}

          {restartRow ? <SystemAudioPermissionRestartRow {...restartRow} /> : null}
        </StyledRowList>
      </DialogContent>
    </MDSModalDialog>
  );
};

const SystemAudioPermissionRow: FC<SystemAudioPermissionModalRow> = ({
  index,
  label,
  supportingText,
  status,
  action,
}) => {
  return (
    <Row>
      <RowMain>
        <Index variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
          {index}.
        </Index>
        <LabelGroup>
          <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.Medium}>
            {label}
          </MDSText>
          {supportingText ? (
            <MDSText variant={MDSTextVariant.TextXs}>{supportingText}</MDSText>
          ) : null}
        </LabelGroup>
      </RowMain>

      <RowActions>
        {status ? (
          <MDSBadge
            label={status.label}
            variant={status.variant}
            icon={status.icon}
            data-testid={`system-audio-permission-status-${index}`}
          />
        ) : null}

        {action ? (
          <RowButton
            variant={action.variant ?? MDSButtonVariant.Contrast}
            onClick={action.onClick}
            isLoading={action.isLoading}
            isDisabled={action.isDisabled}
            data-testid={`system-audio-permission-action-${index}`}
          >
            {action.label}
          </RowButton>
        ) : null}
      </RowActions>
    </Row>
  );
};

const SystemAudioPermissionRestartRow: FC<SystemAudioPermissionModalRestartRow> = ({
  index,
  label,
  actionLabel,
  onClick,
  isLoading,
}) => {
  return (
    <Row>
      <RowMain>
        <Index variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
          {index}.
        </Index>
        <LabelGroup>
          <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.Medium}>
            {label}
          </MDSText>
        </LabelGroup>
      </RowMain>

      <RowActions>
        <RowButton
          variant={MDSButtonVariant.Default}
          onClick={onClick}
          isLoading={isLoading}
          data-testid="system-audio-permission-restart"
        >
          {actionLabel}
        </RowButton>
      </RowActions>
    </Row>
  );
};

const Header = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingRight: theme.spacing.sm,
}));

const DialogContent = styled(MDSDialogTextContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.lg,
}));

const Description = styled(MDSText)(({ theme }) => ({
  color: theme.colors.text.secondary,
}));

const StyledDivider = styled.div(({ theme }) => ({
  margin: `${theme.spacing.lg} 0 ${theme.spacing.sm}`,
  height: 1,
  backgroundColor: theme.colors.border.default,
}));

const StyledRowList = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.sm,
}));

const StyledRowWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.sm,
  padding: `${theme.spacing.sm} 0`,
}));

const StyledRowDivider = styled.div(({ theme }) => ({
  height: 1,
  backgroundColor: theme.colors.border.default,
}));

const Row = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing.md,
  flexWrap: "wrap",
}));

const RowMain = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.md,
  flex: "1 1 auto",
  minWidth: 0,
}));

const Index = styled(MDSText)(({ theme }) => ({
  color: theme.colors.text.tertiary,
  minWidth: theme.spacing["5"],
}));

const LabelGroup = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xs,
  minWidth: 0,
}));

const RowActions = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.spacing.sm,
}));

const RowButton = styled(MDSButton)(({ theme }) => ({
  borderRadius: theme.borderRadius.xlarge,
  paddingInline: theme.spacing.md,
}));
